/**
 * HTML Control
 * Display custom HTML content with comprehensive XSS protection via DOMPurify
 * Implements OWASP-compliant sanitization with multiple security layers
 * Performance: Memoized to prevent unnecessary re-renders
 */

import React, { useMemo, memo } from 'react';
import DOMPurify from 'dompurify';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName } from '../utils/classNameUtils';

/**
 * Whitelist of safe HTML tags for display content
 * Strictly limited to semantic and formatting tags
 * Excludes: script, iframe, form, input, object, embed, applet, meta, link, style
 */
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'a', 'blockquote', 'code', 'pre', 'span', 'div',
  'hr', 'img',
];

/**
 * Whitelist of safe attributes
 * Allows only safe attributes on specific tags
 * - href/title/target/rel on links (target restricted to _blank, _self, _top, _parent)
 * - src/alt/title/width/height on images
 * - class/id on generic containers (for styling only)
 */
const ALLOWED_ATTRIBUTES = {
  'a': ['href', 'title', 'target', 'rel'],
  'img': ['src', 'alt', 'title', 'width', 'height'],
  'span': ['class', 'id'],
  'div': ['class', 'id'],
};

/**
 * Comprehensive DOMPurify configuration
 * Implements defense-in-depth XSS prevention:
 * 1. Whitelist approach (only safe tags/attributes)
 * 2. Multiple hook-based validation layers
 * 3. DOM clobbering prevention
 * 4. Template safety
 * 5. Data URI and protocol blocking
 */
const PURIFY_CONFIG: Record<string, unknown> = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: Object.values(ALLOWED_ATTRIBUTES).flat(),
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  KEEP_CONTENT: true,
  SAFE_FOR_TEMPLATES: true,
  FORCE_BODY: true,
  SANITIZE_DOM: true,
  // Additional security: prevent DOM clobbering and prototype pollution
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM_IMPORT: false,
};

/**
 * Hook to block dangerous protocols in href/src attributes
 * Prevents: javascript:, data:, vbscript:, file:, about:
 */
const blockDangerousProtocols = (node: any, hookEvent: string, config: any) => {
  if (hookEvent !== 'uponSanitizeAttribute') return;

  const hrefAttr = node.getAttribute('href');
  const srcAttr = node.getAttribute('src');
  const actionAttr = node.getAttribute('action');

  // Block dangerous protocols in href
  if (hrefAttr && /^(javascript|data|vbscript|file|about):/i.test(hrefAttr)) {
    node.removeAttribute('href');
  }

  // Block dangerous protocols in src
  if (srcAttr && /^(javascript|data|vbscript|file|about):/i.test(srcAttr)) {
    node.removeAttribute('src');
  }

  // Block form action attributes (prevent form hijacking)
  if (actionAttr) {
    node.removeAttribute('action');
  }
};

/**
 * Hook to remove all event handlers that could trigger code execution
 * Blocks: on* attributes (onclick, onload, onerror, onmouseover, etc.)
 */
const removeEventHandlers = (node: any, hookEvent: string, config: any) => {
  if (hookEvent !== 'uponSanitizeAttribute') return;

  // Get all attributes and check for event handlers
  const attributes = Array.from(node.attributes || []) as Attr[];
  for (const attr of attributes) {
    // Block all on* attributes (event handlers)
    if (attr.name.toLowerCase().startsWith('on')) {
      node.removeAttribute(attr.name);
    }
    // Also block common XSS vectors in data attributes
    if (attr.name.toLowerCase().startsWith('data-') &&
        /javascript|alert|eval|expression/i.test(attr.value)) {
      node.removeAttribute(attr.name);
    }
  }
};

/**
 * Hook to prevent SVG-based XSS attacks
 * SVG can contain scripts and event handlers - remove SVG entirely
 */
const blockSvgXss = (node: any, hookEvent: string, config: any): void => {
  if (hookEvent !== 'uponSanitizeElement') return;

  const nodeName = node.nodeName?.toLowerCase();

  // Block SVG and related elements
  if (nodeName === 'svg' || nodeName === 'animate' || nodeName === 'image' ||
      nodeName === 'foreignobject' || nodeName === 'use') {
    node.parentNode?.removeChild(node); // Remove the element
  }
};

/**
 * Hook to prevent CSS injection attacks
 * Blocks style tags and potentially dangerous CSS rules
 */
const blockCssInjection = (node: any, hookEvent: string, config: any): void => {
  if (hookEvent !== 'uponSanitizeElement') return;

  const nodeName = node.nodeName?.toLowerCase();

  // Block style tags completely (CSS can execute javascript via url())
  if (nodeName === 'style') {
    node.parentNode?.removeChild(node);
    return;
  }

  // Block style attributes with dangerous CSS
  if (node.hasAttribute('style')) {
    const styleAttr = node.getAttribute('style') || '';
    if (/javascript|url\s*\(|expression|@import|binding/i.test(styleAttr)) {
      node.removeAttribute('style');
    }
  }
};

/**
 * Initialize DOMPurify hooks for comprehensive XSS prevention
 * Must be called once to register all custom validation hooks
 */
const initializeDOMPurifyHooks = () => {
  // Register hooks with DOMPurify
  DOMPurify.addHook('uponSanitizeAttribute' as any, blockDangerousProtocols as any);
  DOMPurify.addHook('uponSanitizeAttribute' as any, removeEventHandlers as any);
  DOMPurify.addHook('uponSanitizeElement' as any, blockSvgXss as any);
  DOMPurify.addHook('uponSanitizeElement' as any, blockCssInjection as any);
};

// Initialize hooks on module load
initializeDOMPurifyHooks();

/**
 * Sanitize HTML using DOMPurify library with custom hooks
 * Removes XSS injection vectors while preserving safe formatting
 *
 * Defense-in-depth approach:
 * 1. Whitelist approach (only safe tags/attributes allowed)
 * 2. Protocol validation (blocks javascript:, data:, etc.)
 * 3. Event handler removal (blocks on*)
 * 4. SVG injection prevention (blocks SVG elements)
 * 5. CSS injection prevention (blocks style tags and dangerous CSS)
 * 6. DOM clobbering prevention (SANITIZE_DOM config)
 *
 * @param html HTML string to sanitize
 * @returns Safe HTML string
 */
const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    return DOMPurify.sanitize(html, PURIFY_CONFIG) as unknown as string;
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    return '';
  }
};

const HtmlControl = memo<ControlRendererProps>(({
  control,
  value,
}) => {
  const containerClassName = getBaseControlClassName(control, {
    disabled: control.disabled,
  });

  // Get HTML from value or config
  const html = (value || control.config?.html || control.defaultValue) as string;

  const sanitizedHtml = useMemo(() => {
    if (!html) return '';
    return sanitizeHtml(html);
  }, [html]);

  return (
    <div className={containerClassName}>
      {control.label && (
        <div className="control__label">{control.label}</div>
      )}

      <div
        id={control.id}
        className="control__html-content"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />

      {control.helpText && (
        <div className="control__help">{control.helpText}</div>
      )}
    </div>
  );
});

HtmlControl.displayName = 'HtmlControl';

export default HtmlControl;
